import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { AlertTriangle, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useSyncStore } from '../../lib/offline/sync-store';
import { fromDatetimeLocal, toDatetimeLocal } from '../../lib/datetime-local';
import { LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure } from '../../lib/load-failure';
import LoadFailureNotice from '../../components/ui/LoadFailureNotice';
import SignatureCapture from '../../components/shared/SignatureCapture';
import {
  type Checklist, type DefectSeverity, type ItemResult, type LastPostTrip, type TripInspectionType,
  DRIVER_NOT_LINKED_FALLBACK, ITEM_RESULT_LABEL, TRIP_TYPES, TRIP_TYPE_LABEL, isDriverNotLinked, personName, requireData, tripErrorMessage,
} from '../../lib/trip-inspections';
import LastPostTripReview from './LastPostTripReview';

/**
 * File a pre-trip or post-trip inspection — the driver's own, or a paper one
 * the office keys in for a chosen driver.
 *
 * PRE-TRIP OR POST-TRIP (api 0437) is chosen at the top, preset by the entry
 * the driver came from; nothing is assumed when it is not. Both use the same
 * checklist and the same defect path. A PRE-TRIP shows each chosen unit's last
 * post-trip — at or before the inspection time, so an office paper entry for
 * yesterday reviews what was last then — with its defects and their
 * certification, and the driver acknowledges each one, every time one exists.
 * The server re-checks "last" under the unit lock and answers 409 if a newer
 * post-trip arrived; that refetches the panel so the new one can be reviewed.
 *
 * WHO THE DRIVER IS is the server's decision (api#855): the caller's own
 * `trucking_drivers` row, or, for the office only, the `driver_id` it names.
 * This form never guesses one. In `driver` mode it first asks the list
 * endpoint, which answers 409 for a login with no driver record — and that
 * refusal is shown in the API's own words, in place of the form, because it is
 * the office's to fix and no amount of retrying will change it.
 *
 * THE CHECKLIST IS THE SERVER'S (`GET /checklist`, by the company's country),
 * verbatim, with the regime's own names for the two severities. Every item
 * must be answered. A listed defect is sent by key and the server applies the
 * schedule's severity; a defect not on the list carries the inspector's.
 *
 * A MAJOR DEFECT TAKES THE UNIT OUT OF SERVICE on submit, server-side, until
 * the office certifies it repaired. The form says so before submit rather than
 * after, because that is the moment the driver can still check the call.
 *
 * SIGNATURE. The report id is minted here, so the signature can be uploaded
 * against it before the report exists (entity_type `trip_inspection`, #857).
 * An office paper entry may say it was signed on paper instead.
 *
 * OFFLINE: NOT QUEUED. A refused or unreachable submit keeps every answer on
 * screen and says so. Nothing here pretends to have saved.
 */

export type TripFormMode = 'driver' | 'office';

interface FleetUnit { id: string; unit_number: string; type: string | null; status: string | null; plate: string | null }
interface DriverRow { id: string; first_name: string | null; last_name: string | null; status: string | null }

interface DefectDraft {
  uid: string;
  item: string;
  fleetId: string;
  defectKey: string | null;
  text: string;
  severity: DefectSeverity | '';
  notes: string;
}

const SELF = '__self__';

const inputCls = 'w-full rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text min-h-[44px]';
const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-wider text-fieldiq-muted';
const panelCls = 'rounded-xl border border-fieldiq-border bg-fieldiq-card p-4';

const RESULTS: ItemResult[] = ['no_defect', 'defect', 'not_present'];

export default function TripInspectionForm({ mode, initialType = null, onDone }: {
  mode: TripFormMode;
  /** From the entry the driver came from; null asks. */
  initialType?: TripInspectionType | null;
  onDone: (id: string) => void;
}) {
  const { user } = useAuth();
  const timeZone = useTenantTimeZone();
  const isOnline = useSyncStore((s) => s.isOnline);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const reportId = useMemo(() => uuidv4(), []);

  // ── Who may file: a driver must be linked; the office may not need to be ──
  const linkQuery = useQuery({
    queryKey: ['trip-inspections', 'mine-check'],
    queryFn: () => api.trucking.tripInspections.list().then((r) => r.data?.data ?? []),
    enabled: mode === 'driver',
    retry: false,
  });
  const checklistQuery = useQuery<Checklist>({
    queryKey: ['trip-inspections', 'checklist'],
    queryFn: () => api.trucking.tripInspections.checklist().then((r) => requireData(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const fleetQuery = useQuery<FleetUnit[]>({
    queryKey: ['trip-inspections', 'fleet'],
    queryFn: () => api.trucking.fleet.list().then((r) => r.data?.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const driversQuery = useQuery<DriverRow[]>({
    queryKey: ['trip-inspections', 'drivers'],
    queryFn: () => api.trucking.drivers.list().then((r) => r.data?.data ?? []),
    enabled: mode === 'office',
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const checklist = checklistQuery.data;
  const units = (fleetQuery.data ?? []).filter((u) => u.status !== 'retired');
  const drivers = (driversQuery.data ?? []).filter((d) => d.status === 'active');

  // ── Form state ──
  const [inspectionType, setInspectionType] = useState<TripInspectionType | null>(initialType);
  // Post-trip id acknowledged, per unit. Bound to the id so a newer post-trip
  // reads as not yet reviewed.
  const [acks, setAcks] = useState<Record<string, string>>({});
  const [driverChoice, setDriverChoice] = useState<string>(SELF);
  const [powerId, setPowerId] = useState('');
  const [trailerIds, setTrailerIds] = useState<string[]>([]);
  const [inspectedLocal, setInspectedLocal] = useState(() => toDatetimeLocal(new Date().toISOString(), timeZone));
  const [location, setLocation] = useState('');
  const [odometer, setOdometer] = useState('');
  const [odometerUnit, setOdometerUnit] = useState<'km' | 'mi' | ''>('');
  const [inspectorName, setInspectorName] = useState('');
  const [inspectorTouched, setInspectorTouched] = useState(false);
  const [results, setResults] = useState<Record<string, ItemResult>>({});
  const [defects, setDefects] = useState<DefectDraft[]>([]);
  const [remarks, setRemarks] = useState('');
  const [signatureKey, setSignatureKey] = useState<string | null>(null);
  const [signedOnPaper, setSignedOnPaper] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const forOtherDriver = mode === 'office' && driverChoice !== SELF;
  const chosenDriver = forOtherDriver ? drivers.find((d) => d.id === driverChoice) : undefined;
  const ownName = personName(user?.first_name, user?.last_name);
  // The printed name defaults to whoever the report is for, and stops
  // following once somebody types in it.
  const effectiveInspector = inspectorTouched
    ? inspectorName
    : forOtherDriver
      ? (chosenDriver ? personName(chosenDriver.first_name, chosenDriver.last_name) : '')
      : (ownName === '—' ? '' : ownName);
  const defaultUnit: 'km' | 'mi' = checklist?.regime === 'fmcsa_396_11' ? 'mi' : 'km';
  const effectiveOdoUnit = odometerUnit || defaultUnit;

  const selectedUnits = [
    ...(powerId ? [{ fleet_id: powerId, unit_role: 'power' as const }] : []),
    ...trailerIds.filter((t) => t !== powerId).map((t) => ({ fleet_id: t, unit_role: 'trailer' as const })),
  ];
  const unitById = new Map(units.map((u) => [u.id, u]));
  const unitLabel = (id: string) => unitById.get(id)?.unit_number ?? '—';

  // ── A pre-trip's review of each unit's last post-trip ──
  const isPreTrip = inspectionType === 'pre_trip';
  const reviewBefore = fromDatetimeLocal(inspectedLocal, timeZone);
  const reviewQueries = useQueries({
    queries: selectedUnits.map((u) => ({
      queryKey: ['trip-inspections', 'last-post-trip', u.fleet_id, reviewBefore],
      // `data: null` is the API saying the unit has none; it passes through as
      // null and is never coerced to a report or to a failure.
      queryFn: () => api.trucking.tripInspections.lastPostTrip(u.fleet_id, reviewBefore ?? undefined)
        .then((r): LastPostTrip | null => (r.data && 'data' in r.data ? r.data.data ?? null : null)),
      enabled: isPreTrip && !!reviewBefore,
      ...LOAD_FAILURE_QUERY_OPTIONS,
    })),
  });
  const reviewOf = (fleetId: string) => reviewQueries[selectedUnits.findIndex((u) => u.fleet_id === fleetId)];

  const majorUnits = Array.from(new Set(
    defects
      .filter((d) => (d.defectKey
        ? checklist?.items.find((i) => i.key === d.item)?.defects.find((x) => x.key === d.defectKey)?.severity
        : d.severity) === 'major')
      .map((d) => d.fleetId || selectedUnits[0]?.fleet_id || ''),
  )).filter((id) => selectedUnits.some((u) => u.fleet_id === id));

  // ── Defect editing ──
  const firstUnit = selectedUnits[0]?.fleet_id ?? '';
  const toggleListed = (item: string, key: string) => {
    setDefects((prev) => {
      const has = prev.find((d) => d.item === item && d.defectKey === key);
      if (has) return prev.filter((d) => d !== has);
      return [...prev, { uid: uuidv4(), item, fleetId: firstUnit, defectKey: key, text: '', severity: '', notes: '' }];
    });
  };
  const addOther = (item: string) =>
    setDefects((prev) => [...prev, { uid: uuidv4(), item, fleetId: firstUnit, defectKey: null, text: '', severity: '', notes: '' }]);
  const patchDefect = (uid: string, patch: Partial<DefectDraft>) =>
    setDefects((prev) => prev.map((d) => (d.uid === uid ? { ...d, ...patch } : d)));
  const removeDefect = (uid: string) => setDefects((prev) => prev.filter((d) => d.uid !== uid));
  const setResult = (item: string, r: ItemResult) => {
    setResults((prev) => ({ ...prev, [item]: r }));
    // Defects belong to an item marked defect; changing the answer drops them,
    // or the server would refuse a defect on an item that says it has none.
    if (r !== 'defect') setDefects((prev) => prev.filter((d) => d.item !== item));
  };

  const submit = useMutation({
    mutationFn: async () => {
      const inspectedAt = fromDatetimeLocal(inspectedLocal, timeZone);
      const reviewed = isPreTrip
        ? selectedUnits.flatMap((u) => {
          const last = reviewOf(u.fleet_id)?.data;
          return last && acks[u.fleet_id] === last.id
            ? [{ fleet_id: u.fleet_id, inspection_id: last.id, acknowledged: true }]
            : [];
        })
        : [];
      const body: Record<string, unknown> = {
        id: reportId,
        inspection_type: inspectionType,
        ...(reviewed.length ? { reviewed_post_trips: reviewed } : {}),
        inspected_at: inspectedAt,
        location: location.trim(),
        inspector_name: effectiveInspector.trim(),
        units: selectedUnits,
        item_results: (checklist?.items ?? []).map((i) => ({ item: i.key, result: results[i.key] })),
        defects: defects.map((d) => ({
          fleet_id: d.fleetId || firstUnit,
          item: d.item,
          ...(d.defectKey ? { defect_key: d.defectKey } : { text: d.text.trim(), severity: d.severity }),
          ...(d.notes.trim() ? { notes: d.notes.trim() } : {}),
        })),
        ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
        ...(odometer.trim() !== '' ? { odometer: Number(odometer), odometer_unit: effectiveOdoUnit } : {}),
        ...(forOtherDriver ? { driver_id: driverChoice } : {}),
        ...(signatureKey ? { signature_key: signatureKey } : {}),
        ...(forOtherDriver && signedOnPaper && !signatureKey ? { signed_on_paper: true } : {}),
      };
      return api.trucking.tripInspections.create(body).then((r) => requireData(r.data));
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['trip-inspections'] });
      await queryClient.invalidateQueries({ queryKey: ['trucking-fleet'] });
      const oos: { unit_number: string }[] = data?.out_of_service ?? [];
      const what = inspectionType ? `${TRIP_TYPE_LABEL[inspectionType]} inspection` : 'Trip inspection';
      toast.success(oos.length
        ? `${what} filed. ${oos.map((u) => u.unit_number).join(', ')} is now out of service.`
        : `${what} filed.`);
      onDone(data.id);
    },
    onError: (err) => {
      // A 409 can mean a newer post-trip arrived for a unit: refetch the
      // review panels so it shows, unacknowledged.
      if ((err as any)?.response?.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ['trip-inspections', 'last-post-trip'] });
      }
      setSubmitError(isDriverNotLinked(err)
        ? tripErrorMessage(err, DRIVER_NOT_LINKED_FALLBACK)
        : tripErrorMessage(err, 'The trip inspection was not filed.'));
    },
  });

  const validate = (): string[] => {
    const out: string[] = [];
    if (!inspectionType) out.push('Choose pre-trip or post-trip.');
    if (forOtherDriver && !chosenDriver) out.push('Choose the driver this paper report is for.');
    if (selectedUnits.length === 0) out.push('Choose at least one unit.');
    if (!fromDatetimeLocal(inspectedLocal, timeZone)) out.push('Enter the date and time of the inspection.');
    if (!location.trim()) out.push('Enter where the inspection was done.');
    if (!effectiveInspector.trim()) out.push('Enter the printed name of the person who inspected.');
    if (odometer.trim() !== '' && !(Number(odometer) >= 0)) out.push('The odometer must be a number of 0 or more.');
    const unanswered = (checklist?.items ?? []).filter((i) => !results[i.key]);
    if (unanswered.length) out.push(`Answer every item. ${unanswered.length} not answered: ${unanswered.map((i) => i.label).join(', ')}.`);
    for (const i of checklist?.items ?? []) {
      if (results[i.key] === 'defect' && !defects.some((d) => d.item === i.key)) out.push(`${i.label} is marked Defect but no defect is recorded.`);
    }
    for (const d of defects) {
      if (!d.defectKey && !d.text.trim()) out.push('Describe every defect you added that is not on the list.');
      if (!d.defectKey && !d.severity) out.push('Choose a severity for every defect you added that is not on the list.');
      if (!selectedUnits.some((u) => u.fleet_id === (d.fleetId || firstUnit))) out.push('Every defect must be on one of the units chosen above.');
    }
    if (isPreTrip) {
      for (const u of selectedUnits) {
        const q = reviewOf(u.fleet_id);
        const n = unitLabel(u.fleet_id);
        if (!q || q.isLoading) out.push(`The last post-trip for ${n} is still loading.`);
        else if (q.error) out.push(`The last post-trip for ${n} did not load, so it cannot be reviewed yet.`);
        else if (q.data && acks[u.fleet_id] !== q.data.id) out.push(`Review and acknowledge the last post-trip for ${n}.`);
      }
    }
    if (!signatureKey && !(forOtherDriver && signedOnPaper)) {
      out.push(forOtherDriver ? 'Capture a signature, or confirm the paper report is signed.' : 'Sign the inspection.');
    }
    return Array.from(new Set(out));
  };

  const onSubmit = () => {
    setSubmitError(null);
    const found = validate();
    setProblems(found);
    if (found.length) return;
    if (!isOnline) {
      setSubmitError('You are offline. This trip inspection is NOT saved — submit it again when you have signal. Your answers are still here.');
      return;
    }
    submit.mutate();
  };

  // ── Gates, in the order a driver would meet them ──
  if (mode === 'driver' && linkQuery.isLoading) return <Centered><Loader2 className="size-5 animate-spin" /> Checking your driver record…</Centered>;
  if (mode === 'driver' && linkQuery.error && isDriverNotLinked(linkQuery.error)) {
    return (
      <div role="alert" data-testid="trip-not-linked" className={`${panelCls} flex items-start gap-3`}>
        <AlertTriangle className="mt-0.5 size-5 shrink-0 text-fieldiq-accent-signal" aria-hidden="true" />
        <p className="text-sm text-fieldiq-text">{tripErrorMessage(linkQuery.error, DRIVER_NOT_LINKED_FALLBACK)}</p>
      </div>
    );
  }
  const checklistFailure = queryLoadFailure(checklistQuery);
  if (checklistFailure) {
    // A 422 here is the API saying no checklist applies to this company's
    // country — say that, rather than a connection notice.
    const status = (checklistQuery.error as any)?.response?.status;
    if (status === 422) {
      return <p role="alert" className={`${panelCls} text-sm text-fieldiq-text`}>{tripErrorMessage(checklistQuery.error, 'No trip inspection checklist applies to this company.')}</p>;
    }
    return <LoadFailureNotice error={checklistQuery.error} kind={checklistFailure} subject="the trip inspection checklist" />;
  }
  if (!checklist) return <Centered><Loader2 className="size-5 animate-spin" /> Loading the checklist…</Centered>;

  const fleetFailure = queryLoadFailure(fleetQuery);

  return (
    <form
      data-testid="trip-inspection-form"
      className="space-y-4"
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
    >
      <p className="text-xs text-fieldiq-muted">{checklist.title} · {checklist.source}</p>

      {/* ── Pre-trip or post-trip ── */}
      <section className={panelCls}>
        <div role="radiogroup" aria-label="Inspection" className="grid grid-cols-2 gap-2">
          {TRIP_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={inspectionType === t}
              onClick={() => setInspectionType(t)}
              className={`min-h-[44px] rounded-lg border px-3 text-sm font-semibold ${inspectionType === t
                ? 'border-black/20 bg-fi-signal-blue text-fi-signal-ink'
                : 'border-fieldiq-border text-fieldiq-text'}`}
            >
              {TRIP_TYPE_LABEL[t]}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-fieldiq-muted">
          {inspectionType === 'pre_trip'
            ? 'Before the unit is driven. Review each unit\'s last post-trip below.'
            : inspectionType === 'post_trip'
              ? 'At the end of the shift.'
              : 'Choose which inspection this is.'}
        </p>
      </section>

      {/* ── Driver (office only) ── */}
      {mode === 'office' && (
        <section className={panelCls}>
          <label htmlFor="trip-driver" className={labelCls}>Driver</label>
          <select id="trip-driver" className={inputCls} value={driverChoice} onChange={(e) => { setDriverChoice(e.target.value); setSignedOnPaper(false); }}>
            <option value={SELF}>Me — my own inspection</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>{personName(d.first_name, d.last_name)} — paper report</option>
            ))}
          </select>
          {queryLoadFailure(driversQuery) && <p className="mt-2 text-sm text-fieldiq-text">The driver list did not load, so only your own inspection can be filed right now.</p>}
          {forOtherDriver && <p className="mt-2 text-xs text-fieldiq-muted">Recorded as entered by the office, on the driver's behalf.</p>}
        </section>
      )}

      {/* ── Units ── */}
      <section className={panelCls}>
        <h2 className="mb-3 text-sm font-semibold text-fieldiq-text">Units</h2>
        {fleetFailure ? (
          <LoadFailureNotice error={fleetQuery.error} kind={fleetFailure} subject="fleet units" />
        ) : fleetQuery.isLoading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : units.length === 0 ? (
          <p className="text-sm text-fieldiq-muted">No fleet units on record. Add units on the Fleet page first.</p>
        ) : (
          <div className="space-y-3">
            <div>
              <label htmlFor="trip-power" className={labelCls}>Power unit</label>
              <select id="trip-power" className={inputCls} value={powerId} onChange={(e) => { setPowerId(e.target.value); setTrailerIds((t) => t.filter((x) => x !== e.target.value)); }}>
                <option value="">None — trailer only</option>
                {units.map((u) => <option key={u.id} value={u.id}>{unitOption(u)}</option>)}
              </select>
            </div>
            <fieldset>
              <legend className={labelCls}>Trailers</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {units.filter((u) => u.id !== powerId).map((u) => (
                  <label key={u.id} className="flex min-h-[44px] items-center gap-2 rounded-lg border border-fieldiq-border px-3 text-sm text-fieldiq-text">
                    <input
                      type="checkbox"
                      checked={trailerIds.includes(u.id)}
                      onChange={(e) => setTrailerIds((t) => (e.target.checked ? [...t, u.id] : t.filter((x) => x !== u.id)))}
                    />
                    {unitOption(u)}
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        )}
      </section>

      {/* ── When, where, who ── */}
      <section className={`${panelCls} grid gap-3 sm:grid-cols-2`}>
        <div>
          <label htmlFor="trip-when" className={labelCls}>Date and time</label>
          <input id="trip-when" type="datetime-local" className={inputCls} value={inspectedLocal} onChange={(e) => setInspectedLocal(e.target.value)} />
        </div>
        <div>
          <label htmlFor="trip-location" className={labelCls}>Location</label>
          <input id="trip-location" className={inputCls} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, town, or highway" />
        </div>
        <div>
          <label htmlFor="trip-odometer" className={labelCls}>Odometer (if equipped)</label>
          <div className="flex gap-2">
            <input id="trip-odometer" inputMode="decimal" className={inputCls} value={odometer} onChange={(e) => setOdometer(e.target.value)} />
            <select aria-label="Odometer unit" className={`${inputCls} w-24`} value={effectiveOdoUnit} onChange={(e) => setOdometerUnit(e.target.value as 'km' | 'mi')}>
              <option value="km">km</option>
              <option value="mi">mi</option>
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="trip-inspector" className={labelCls}>Inspector's printed name</label>
          <input id="trip-inspector" className={inputCls} value={effectiveInspector} onChange={(e) => { setInspectorTouched(true); setInspectorName(e.target.value); }} />
        </div>
      </section>

      {/* ── The last post-trip, per unit (pre-trip only) ── */}
      {isPreTrip && selectedUnits.length > 0 && (
        <section className={`${panelCls} space-y-3`} data-testid="trip-review-panel">
          <h2 className="text-sm font-semibold text-fieldiq-text">Review the last post-trip</h2>
          {selectedUnits.map((u) => {
            const q = reviewOf(u.fleet_id);
            return (
              <LastPostTripReview
                key={u.fleet_id}
                unitNumber={unitLabel(u.fleet_id)}
                state={{ data: q?.data, error: q?.error, isLoading: !!q?.isLoading }}
                checklist={checklist}
                timeZone={timeZone}
                acknowledged={!!q?.data && acks[u.fleet_id] === q.data.id}
                onAcknowledge={(on) => setAcks((prev) => {
                  const next = { ...prev };
                  if (on && q?.data) next[u.fleet_id] = q.data.id; else delete next[u.fleet_id];
                  return next;
                })}
              />
            );
          })}
        </section>
      )}

      {/* ── The checklist ── */}
      <section className={panelCls}>
        <h2 className="mb-3 text-sm font-semibold text-fieldiq-text">Inspection items</h2>
        <ol className="space-y-3">
          {checklist.items.map((item, idx) => {
            const result = results[item.key];
            const itemDefects = defects.filter((d) => d.item === item.key);
            return (
              <li key={item.key} data-testid={`trip-item-${item.key}`} className="rounded-lg border border-fieldiq-border bg-fieldiq-dark p-3">
                <p className="text-sm font-semibold text-fieldiq-text">{idx + 1}. {item.label}</p>
                <div role="radiogroup" aria-label={item.label} className="mt-2 grid grid-cols-3 gap-2">
                  {RESULTS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      role="radio"
                      aria-checked={result === r}
                      onClick={() => setResult(item.key, r)}
                      className={`min-h-[40px] rounded-lg border px-2 text-xs font-semibold ${result === r
                        ? (r === 'defect' ? 'border-black/20 bg-fi-signal-amber text-fi-signal-ink' : 'border-black/20 bg-fi-signal-green text-fi-signal-ink')
                        : 'border-fieldiq-border text-fieldiq-text'}`}
                    >
                      {ITEM_RESULT_LABEL[r]}
                    </button>
                  ))}
                </div>

                {result === 'defect' && (
                  <div className="mt-3 space-y-2">
                    {(['minor', 'major'] as DefectSeverity[]).map((sev) => {
                      const listed = item.defects.filter((d) => d.severity === sev);
                      if (listed.length === 0) return null;
                      return (
                        <fieldset key={sev}>
                          <legend className="text-[0.68rem] font-black uppercase tracking-[0.06em] text-fieldiq-muted">{checklist.severity_labels[sev]}</legend>
                          {listed.map((d) => {
                            const chosen = itemDefects.find((x) => x.defectKey === d.key);
                            return (
                              <div key={d.key} className="mt-1">
                                <label className="flex items-start gap-2 text-sm text-fieldiq-text">
                                  <input type="checkbox" className="mt-1" checked={!!chosen} onChange={() => toggleListed(item.key, d.key)} />
                                  <span>{d.qualifier ? <em className="text-fieldiq-muted">{d.qualifier} </em> : null}{d.text}{d.footnote ? <span className="text-fieldiq-muted"> ({d.footnote})</span> : null}</span>
                                </label>
                                {chosen && <DefectExtras d={chosen} units={selectedUnits.map((u) => u.fleet_id)} unitLabel={unitLabel} onPatch={patchDefect} />}
                              </div>
                            );
                          })}
                        </fieldset>
                      );
                    })}
                    {itemDefects.filter((d) => !d.defectKey).map((d) => (
                      <div key={d.uid} className="space-y-2 rounded-lg border border-fieldiq-border p-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-fieldiq-muted">{item.defects.length ? 'Defect not on the list' : 'Defect'}</span>
                          <button type="button" aria-label="Remove this defect" onClick={() => removeDefect(d.uid)} className="text-fieldiq-muted"><Trash2 className="size-4" /></button>
                        </div>
                        <input aria-label="Describe the defect" className={inputCls} value={d.text} onChange={(e) => patchDefect(d.uid, { text: e.target.value })} placeholder="What is wrong" />
                        <div role="radiogroup" aria-label="Severity" className="grid grid-cols-2 gap-2">
                          {(['minor', 'major'] as DefectSeverity[]).map((sev) => (
                            <button key={sev} type="button" role="radio" aria-checked={d.severity === sev} onClick={() => patchDefect(d.uid, { severity: sev })}
                              className={`min-h-[40px] rounded-lg border px-2 text-xs font-semibold ${d.severity === sev
                                ? (sev === 'major' ? 'border-black/20 bg-fi-signal-red text-fi-signal-ink' : 'border-black/20 bg-fi-signal-amber text-fi-signal-ink')
                                : 'border-fieldiq-border text-fieldiq-text'}`}>
                              {checklist.severity_labels[sev]}
                            </button>
                          ))}
                        </div>
                        <DefectExtras d={d} units={selectedUnits.map((u) => u.fleet_id)} unitLabel={unitLabel} onPatch={patchDefect} />
                      </div>
                    ))}
                    <button type="button" onClick={() => addOther(item.key)} className="inline-flex items-center gap-1 text-xs font-semibold text-fieldiq-accent-signal">
                      <Plus className="size-3.5" /> {item.defects.length ? 'Add a defect not on the list' : 'Add a defect'}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </section>

      <section className={panelCls}>
        <label htmlFor="trip-remarks" className={labelCls}>Remarks</label>
        <textarea id="trip-remarks" className={`${inputCls} min-h-[80px]`} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </section>

      {/* ── Signature ── */}
      <section className={panelCls}>
        <SignatureCapture
          label={forOtherDriver ? "Driver's signature" : 'Your signature'}
          formName="trip-inspection"
          submissionId={reportId}
          entityType="trip_inspection"
          onSigned={(key) => setSignatureKey(key)}
          onCleared={() => setSignatureKey(null)}
        />
        {forOtherDriver && (
          <label className="mt-3 flex min-h-[44px] items-center gap-2 text-sm text-fieldiq-text">
            <input type="checkbox" checked={signedOnPaper} onChange={(e) => setSignedOnPaper(e.target.checked)} />
            The paper report is signed by the driver
          </label>
        )}
      </section>

      {majorUnits.length > 0 && (
        <div role="note" data-testid="trip-oos-warning" className="flex items-start gap-3 rounded-xl border border-fieldiq-border bg-fi-red/10 p-4">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-fieldiq-text" aria-hidden="true" />
          <p className="text-sm text-fieldiq-text">
            A major defect is recorded. Submitting takes <strong>{majorUnits.map(unitLabel).join(', ')}</strong> out of service until the office certifies the repair.
          </p>
        </div>
      )}

      {problems.length > 0 && (
        <ul role="alert" data-testid="trip-problems" className="list-disc space-y-1 rounded-xl border border-fieldiq-border bg-fieldiq-card p-4 pl-8 text-sm text-fieldiq-text">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      {submitError && (
        <p role="alert" data-testid="trip-submit-error" className="rounded-xl border border-fieldiq-border bg-fieldiq-card p-4 text-sm text-fieldiq-text">{submitError}</p>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => navigate(-1)} className="fi-action fi-action--standard border border-fieldiq-border bg-fieldiq-card text-fieldiq-text">Cancel</button>
        <button type="submit" disabled={submit.isPending} className="fi-action fi-action--standard fi-action--primary disabled:opacity-50">
          {submit.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          File inspection
        </button>
      </div>
    </form>
  );
}

function unitOption(u: FleetUnit) {
  const bits = [u.unit_number, u.type, u.plate].filter((x) => x != null && String(x).trim() !== '');
  return `${bits.join(' · ')}${u.status === 'out_of_service' ? ' — out of service' : ''}`;
}

function DefectExtras({ d, units, unitLabel, onPatch }: {
  d: DefectDraft;
  units: string[];
  unitLabel: (id: string) => string;
  onPatch: (uid: string, patch: Partial<DefectDraft>) => void;
}) {
  return (
    <div className="ml-6 mt-1 grid gap-2 sm:grid-cols-2">
      {units.length > 1 && (
        <select aria-label="Unit with this defect" className={inputCls} value={units.includes(d.fleetId) ? d.fleetId : units[0]} onChange={(e) => onPatch(d.uid, { fleetId: e.target.value })}>
          {units.map((id) => <option key={id} value={id}>{unitLabel(id)}</option>)}
        </select>
      )}
      <input aria-label="Notes on this defect" className={inputCls} value={d.notes} onChange={(e) => onPatch(d.uid, { notes: e.target.value })} placeholder="Notes (optional)" />
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-center gap-2 py-12 text-sm text-fieldiq-muted">{children}</div>;
}
